'use client'

import React, { useEffect, useRef, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import IconButton from '@mui/material/IconButton'

import { useUIStore } from '@/stores/uiStore'

const RajalRanap = React.lazy(() => import('@/views/kunjungan/RajalRanap'))

const PulangRajal = React.lazy(() => import('@/views/kunjungan/PulangRajal'))
const PulangRanap = React.lazy(() => import('@/views/kunjungan/PulangRanap'))
const RawatInap = React.lazy(() => import('@/views/kunjungan/RawatInap'))
const RawatJalan = React.lazy(() => import('@/views/kunjungan/RawatJalan'))
const RawatHome = React.lazy(() => import('@/views/rawat/RawatHome'))
const Pendapatan = React.lazy(() => import('@/views/pendapatan/Pendapatan'))
const PendapatanRincian = React.lazy(() => import('@/views/pendapatan/PendapatanRincian'))
const Biaya = React.lazy(() => import('@/views/biaya/Biaya'))
const PendapatanBiaya = React.lazy(() => import('@/views/pendapatan/PendapatanBiaya'))
const VerifikasiBillingBPJS = React.lazy(() => import('@/views/pendapatan/VerifikasiBillingBPJS'))
const Bedah = React.lazy(() => import('@/views/operasi/Bedah'))
const Anastesi = React.lazy(() => import('@/views/operasi/Anastesi'))
const Spesialisasi = React.lazy(() => import('@/views/operasi/Spesialisasi'))
const PenyakitTerbanyak = React.lazy(() => import('@/views/rekammedis/PenyakitTerbanyak'))
const PulangHome = React.lazy(() => import('@/views/pulang/PulangHome'))
const PendapatanTiapDokter = React.lazy(() => import('@/views/pendapatan/PendapatanTiapDokter'))

const WelcomePage = React.lazy(() => import('./WelcomePage'))

const componentMapping: Record<string, React.ComponentType<any>> = {
  'Kunjungan Rawat Jalan': RawatJalan,
  'Kunjungan Rawat Inap': RawatInap,
  'Kunjungan Rajal & Ranap': RajalRanap,
  'Pulang Rajal': PulangRajal,
  'Pulang Ranap': PulangRanap,
  Perawatan: RawatHome,
  'Pasien Pulang': PulangHome,
  'Pendapatan Tahunan': Pendapatan,
  'Pendapatan Rincian': PendapatanRincian,
  'Biaya Tahunan': Biaya,
  'Pendapatan & Biaya': PendapatanBiaya,
  'Verifikasi Billing BPJS': VerifikasiBillingBPJS,
  Bedah: Bedah,
  Anastesi: Anastesi,
  Spesialisasi: Spesialisasi,
  'Penyakit Terbanyak': PenyakitTerbanyak,
  'Pendapatan Tiap Dokter': PendapatanTiapDokter
}

// TabType imported from store

const Home = () => {
  const tabs = useUIStore(state => state.tabsByUsaha[state.selectedUsaha] ?? [])
  const currentTab = useUIStore(state => state.currentTabByUsaha[state.selectedUsaha] ?? 0)
  const setCurrentTab = useUIStore(state => state.setActiveCurrentTab)
  const hydrate = useUIStore(state => state.hydrateFromStorage)
  const removeActiveTab = useUIStore(state => state.removeActiveTab)
  const reorderActiveTabs = useUIStore(state => state.reorderActiveTabs)

  const dragItem = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    hydrate()
  }, [hydrate])

  const handleDragStart = (e: React.DragEvent, index: number) => {
    const draggedTab = e.currentTarget.cloneNode(true) as HTMLElement

    draggedTab.style.position = 'absolute'
    draggedTab.style.top = '-1000px'
    draggedTab.style.opacity = '0.5'
    document.body.appendChild(draggedTab)

    e.dataTransfer.setDragImage(draggedTab, 0, 0)

    setTimeout(() => {
      document.body.removeChild(draggedTab)
    }, 0)

    dragItem.current = index
    setIsDragging(true)
  }

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index
  }

  const handleDragEnd = () => {
    setIsDragging(false)

    if (dragItem.current !== null && dragOverItem.current !== null) {
      reorderActiveTabs(dragItem.current, dragOverItem.current)
    }

    dragItem.current = null
    dragOverItem.current = null
  }

  useEffect(() => {
    hydrate()
  }, [hydrate])

  const handleClose = (index: number) => (e: React.MouseEvent) => {
    e.stopPropagation()
    removeActiveTab(index)
  }

  useEffect(() => {
    const container = scrollRef.current
    const el = tabRefs.current[currentTab]

    if (!container || !el) return
    const targetLeft = el.offsetLeft - container.clientWidth / 2 + el.offsetWidth / 2

    container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' })
  }, [currentTab, tabs.length])

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: '100%' },
        overflow: { xs: 'visible', md: 'hidden' }
      }}
    >
      {tabs.length > 0 && (
        <Card
          ref={scrollRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollBehavior: 'smooth',
            flexWrap: 'nowrap',
            flexShrink: 0,
            bgcolor: 'grey.200',
            p: 1,
            gap: 1,
            '&::-webkit-scrollbar': {
              height: '10px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme => theme.palette.grey[500],
              borderRadius: '6px',
              '&:hover': {
                background: theme => theme.palette.grey[600]
              }
            }
          }}
        >
          {tabs.map((tab, index) => (
            <div
              key={tab.id}
              ref={el => {
                tabRefs.current[index] = el
              }}
              draggable
              onDragStart={e => handleDragStart(e, index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
              style={{
                opacity: isDragging && dragItem.current === index ? 0.5 : 1,
                cursor: 'move',
                flex: '0 0 auto'
              }}
            >
              <Box
                onClick={() => setCurrentTab(index)}
                onMouseUp={e => {
                  if (e.button === 1) {
                    e.preventDefault()
                    e.stopPropagation()
                    removeActiveTab(index)
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minWidth: 'auto',
                  px: 2,
                  bgcolor: theme => (currentTab === index ? theme.palette.primary.main : theme.palette.grey[300]),
                  color: theme => (currentTab === index ? theme.palette.common.white : theme.palette.grey[800]),
                  borderRadius: 1,
                  minHeight: '34px',
                  transition: 'all 0.2s',
                  '&:hover': theme => ({
                    bgcolor: currentTab === index ? theme.palette.primary.dark : (theme.palette.grey as any)[400],
                    color: currentTab === index ? theme.palette.common.white : (theme.palette.grey as any)[900]
                  }),
                  boxShadow: currentTab === index ? 2 : 0,
                  userSelect: 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                  <Box
                    component='span'
                    sx={{
                      fontWeight: 600,
                      color: theme => (currentTab === index ? theme.palette.common.white : theme.palette.grey[800])
                    }}
                  >
                    {tab.label}
                  </Box>
                  <IconButton
                    size='small'
                    onClick={handleClose(index)}
                    sx={theme => ({
                      padding: 0.25,
                      borderRadius: '50%',
                      marginLeft: 'auto',
                      color: currentTab === index ? theme.palette.common.white : theme.palette.grey[800],
                      '&:hover': {
                        bgcolor: currentTab === index ? theme.palette.primary.dark : (theme.palette.grey as any)[500],
                        color: theme.palette.common.white
                      }
                    })}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </Box>
              </Box>
            </div>
          ))}
        </Card>
      )}

      <Box
        sx={{
          mt: 2,
          flexGrow: 1,
          flex: '1 1 auto',
          overflow: { xs: 'visible', md: 'hidden' },
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {tabs.length === 0 ? (
          <WelcomePage />
        ) : (
          tabs.map((tab, index) => {
            const Component = componentMapping[tab?.label || '']

            return (
              <Box
                key={tab.id}
                sx={{
                  display: currentTab === index ? 'flex' : 'none',
                  flexDirection: 'column',
                  flexGrow: 1,
                  height: { xs: 'auto', md: '100%' },
                  minHeight: 0,
                  overflow: { xs: 'visible', md: 'hidden' }
                }}
              >
                {Component ? <Component isActive={currentTab === index} /> : <div>Content not found</div>}
              </Box>
            )
          })
        )}
      </Box>
    </Box>
  )
}

export default Home
